import {
  CHECKOUT_ID_STORAGE_KEY,
  createIdempotencyKey,
  createPurchaseIntent,
  isActivePaidMembership,
  PURCHASE_INTENT_STORAGE_KEY,
  parsePurchaseIntent,
  resolveCheckoutState,
} from './membership-popup-core.js';
import {
  logEvent,
  getExperiment as statsigGetExperiment,
} from './util.statsig.js';

const EXPERIMENT_NAME = 'promotion_popup';
const HIDE_GIFT_BOX_EXPERIMENT_NAME = 'hide_gift_box';
const PROMOTION_POPUP_GROUP_IDS = { old: 31, new: 32, none: 33 };
const HIDE_GIFT_BOX_GROUP_IDS = { false: 35, true: 36 };
const STORAGE_KEY_PREFIX = 'newsletter_popup_';
const MEMBERSHIP_EXPERIMENT_NAME = 'show_vip_banner';
const MEMBERSHIP_SDK_FALLBACK_URL =
  'https://static.airwallex.com/components/sdk/v1/index.js';
const CHECKOUT_CACHE_TTL_MS = 5 * 60 * 1000;
const CTA_LOADING_STORAGE_KEY = 'jjb_membership_cta_loading';
const CHECKOUT_SOURCE_STORAGE_KEY_PREFIX = 'jjb_membership_checkout_source_';

function isTestMode() {
  let shopMode = '';
  try {
    shopMode = window.localStorage.getItem('_shop_mode') || '';
  } catch {}
  const hostname = window.location.hostname;
  return (
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1') ||
    hostname.includes('shopifypreview.com') ||
    shopMode === 'test'
  );
}

function getExperiment(experimentName) {
  if (isTestMode() && window.jjbUtils?.overrideExperiment) {
    return window.jjbUtils.overrideExperiment(experimentName);
  }
  return statsigGetExperiment(experimentName);
}

function getMembershipApiPrefix() {
  return `${isTestMode() ? '/apps/ai-test' : '/apps/ai'}/membership`;
}

function normalizeEntryPage(value) {
  return value || 'direct';
}

async function requestMembership(path, options = {}) {
  const response = await fetch(getMembershipApiPrefix() + path, {
    method: options.method || 'GET',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload || payload.success === false) {
    const error = new Error(
      payload?.message || payload?.errorMessage || 'Membership request failed.',
    );
    error.code = payload?.code;
    error.status = response.status;
    throw error;
  }
  return payload.data;
}

function showCheckoutRequestError(error) {
  if (typeof window.toast !== 'function') return;
  if (error?.status === 400 && String(error.code) === '1027') {
    window.toast(
      'Your membership information is syncing. Please refresh the page.',
      10000,
    );
    return;
  }
  if (error?.status === 429) {
    window.toast(
      'Please refresh the page and complete the human verification process.',
      10000,
    );
    return;
  }
  if (error?.status >= 500 && error.status <= 599) {
    window.toast(
      `${error.message || 'Unable to start secure checkout.'} Please take a screenshot and contact customer support.`,
      10000,
    );
  }
}

function trackMembership(eventName, params = {}, options = {}) {
  window?.zlog?.track?.(
    eventName,
    { page: window.location.pathname, ...params },
    { statsig: false, monitor: false, ...options },
  );
}

function getAirwallexPaymentUrl(data) {
  return data?.url || '';
}

function getAirwallexErrorParams(error, checkoutId) {
  return {
    checkout_id: checkoutId || undefined,
    error_code: error?.code || error?.errorCode || undefined,
    error_message: error?.message || String(error || 'Unknown Airwallex error'),
  };
}

function getThemeMembershipState() {
  return (
    window.theme?.membershipState ||
    window.JJB_CUSTOMER_MEMBERSHIP_STATE ||
    null
  );
}

function isLoggedIn() {
  return Boolean(window.theme?.customerId);
}

function trackCheckoutMonitor(eventName, params = {}) {
  trackMembership(
    eventName,
    { customerId: window.theme?.customerId || null, ...params },
    { monitor: true },
  );
}

function getSafeReturnUrl() {
  return (
    window.location.pathname + window.location.search + window.location.hash
  );
}

function createCheckoutSourceHash() {
  if (typeof window.crypto?.randomUUID === 'function') {
    return `h_${window.crypto.randomUUID()}`;
  }
  return `h_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

function storeCheckoutSource(source) {
  const hash = createCheckoutSourceHash();
  try {
    window.localStorage.setItem(
      CHECKOUT_SOURCE_STORAGE_KEY_PREFIX + hash,
      source,
    );
  } catch {}
  return hash;
}

function getLoginUrl(returnUrl) {
  return `/customer_authentication/login?return_to=${encodeURIComponent(returnUrl)}`;
}

function getCheckoutSuccessUrl(planType = 'pro') {
  const url = new URL(
    '/pages/checkout-succeed',
    'https://exn11x-id.myshopify.com',
  );
  url.searchParams.set('source', storeCheckoutSource(getSafeReturnUrl()));
  url.searchParams.set('plan', planType === 'premium' ? 'PREMIUM' : 'PRO');
  return url.href;
}

let airwallexSdkPromise;
function loadAirwallexSdk(url) {
  const sdk = window.AirwallexComponentsSDK || window.Airwallex;
  if (sdk) return Promise.resolve(sdk);
  if (airwallexSdkPromise) return airwallexSdkPromise;
  airwallexSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url || MEMBERSHIP_SDK_FALLBACK_URL;
    script.async = true;
    script.onload = () =>
      window.AirwallexComponentsSDK || window.Airwallex
        ? resolve(window.AirwallexComponentsSDK || window.Airwallex)
        : reject(new Error('Airwallex SDK unavailable.'));
    script.onerror = () =>
      reject(new Error('Unable to load the payment form.'));
    document.head.appendChild(script);
  }).catch((error) => {
    airwallexSdkPromise = undefined;
    throw error;
  });
  return airwallexSdkPromise;
}

function createMembershipPopupController() {
  const root = document.querySelector('[data-jjb-membership-popup]');
  if (!root) return null;
  const offer = root.querySelector('[data-jjb-membership-offer]');
  const checkout = root.querySelector('[data-jjb-membership-checkout]');
  const resultDialog = root.querySelector('[data-jjb-membership-result]');
  const mount = root.querySelector('[data-jjb-membership-checkout-mount]');
  const paymentFormHost = root.querySelector(
    '[data-jjb-membership-payment-form-host]',
  );
  const checkoutSubtotal = root.querySelector(
    '[data-jjb-membership-checkout-subtotal]',
  );
  const checkoutSubtotalAmount = root.querySelector(
    '[data-jjb-membership-checkout-subtotal-amount]',
  );
  const checkoutAdjustment = root.querySelector(
    '[data-jjb-membership-checkout-adjustment]',
  );
  const checkoutAdjustmentAmount = root.querySelector(
    '[data-jjb-membership-checkout-adjustment-amount]',
  );
  const checkoutLoading = root.querySelector(
    '[data-jjb-membership-checkout-loading]',
  );
  const status = root.querySelector('[data-jjb-membership-checkout-status]');
  const retry = root.querySelector('[data-jjb-membership-checkout-retry]');
  const checkoutClose = root.querySelector(
    '[data-jjb-membership-checkout-close]',
  );
  const planLabel = root.querySelector('[data-jjb-membership-checkout-plan]');
  const priceLabel = root.querySelector('[data-jjb-membership-checkout-price]');
  const billingLabel = root.querySelector(
    '.jjb-membership-checkout__billing-label',
  );
  const resultMessage = root.querySelector(
    '[data-jjb-membership-result-message]',
  );
  const resultRetry = root.querySelector('[data-jjb-membership-result-retry]');
  const resultClose = root.querySelector('[data-jjb-membership-result-close]');
  let context = null;
  let latestSdkError = null;
  let latestPaymentError = null;
  let sdkLoadStartedAt = 0;
  let checkoutState = 'closed';
  let activeRequest = 0;
  let abortController = null;
  let paymentElement = null;
  let idempotencyKey = '';
  let previousFocus = null;
  let pollingTimer;
  let pollingStartedAt = 0;
  let successCloseTimer;
  const begunCheckoutIds = new Set();
  const designMode = root.dataset.designMode === 'true';
  const popupSectionId = root.dataset.popupSectionId || '';
  const popupDelayDays = getDelayDays(root);
  let hiddenShopifyChat = [];
  let ctaLoading = false;

  function hideShopifyChat() {
    if (hiddenShopifyChat.length) return;
    hiddenShopifyChat = [...document.querySelectorAll('shopify-chat')].map(
      (element) => {
        const display = element.style.display;
        element.style.display = 'none';
        return { element, display };
      },
    );
  }

  function restoreShopifyChat() {
    hiddenShopifyChat.forEach(({ element, display }) => {
      element.style.display = display;
    });
    hiddenShopifyChat = [];
  }

  function normalizeCheckoutBillingCycle(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase();
    return normalized === 'year' || normalized === 'yearly'
      ? 'yearly'
      : 'monthly';
  }

  function visibleDialog() {
    if (!resultDialog.hidden) return resultDialog;
    return checkout.hidden
      ? root.querySelector('[data-jjb-membership-popup-view]:not([hidden])')
      : checkout;
  }

  function syncView() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    root
      .querySelectorAll('[data-jjb-membership-popup-view]')
      .forEach((view) => {
        view.hidden =
          view.dataset.jjbMembershipPopupView === 'mobile' ? !mobile : mobile;
      });
  }

  function lockPage(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  function setCheckoutState(nextState, message) {
    checkoutState = nextState;
    root.dataset.checkoutState = nextState;
    status.innerHTML = message || '';
    checkoutClose.disabled =
      nextState === 'submitting' || nextState === 'confirming';
    retry.hidden =
      nextState !== 'init_error' && nextState !== 'confirm_timeout';
    retry.textContent =
      nextState === 'confirm_timeout' ? 'Check status' : 'Try again';
    checkoutLoading.hidden = nextState !== 'checkout_loading';
    if (nextState !== 'ready') mount.hidden = true;
  }

  function showResult(state, message, retryLabel = '') {
    destroyPaymentElement();
    checkout.hidden = true;
    offer.hidden = true;
    resultDialog.hidden = false;
    resultDialog.dataset.resultState = state;
    resultMessage.innerHTML = typeof message === 'string' ? message : '';
    resultRetry.hidden = !retryLabel;
    resultRetry.textContent = retryLabel;
    resultClose.disabled = state === 'processing';
    focusDialog();
  }

  function formatCheckoutAmount(data) {
    const chargeCents = Number(data?.chargeCents);
    const currency = String(data?.currency || '').toUpperCase();
    if (
      !Number.isInteger(chargeCents) ||
      chargeCents < 0 ||
      !/^[A-Z]{3}$/.test(currency)
    )
      return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(chargeCents / 100);
    } catch {
      return `${currency} ${(chargeCents / 100).toFixed(2)}`;
    }
  }

  function syncCheckoutAmount(data) {
    const formattedAmount = formatCheckoutAmount(data);
    const creditCents = Number(data?.creditCents);
    const formattedCredit =
      Number.isInteger(creditCents) && creditCents > 0
        ? formatCheckoutAmount({
            chargeCents: creditCents,
            currency: data?.currency,
          })
        : '';
    checkoutSubtotalAmount.textContent = formattedAmount;
    checkoutSubtotal.hidden = !formattedAmount;
    checkoutAdjustmentAmount.textContent = formattedCredit
      ? `-${formattedCredit}`
      : '';
    checkoutAdjustment.hidden = !formattedCredit;
    checkoutSubtotal.dataset.checkoutMode = String(data?.mode || '');
    checkoutSubtotal.dataset.creditCents = String(data?.creditCents ?? '');
  }

  function getResultPlanLabel(label) {
    const fallback = context.plan_type === 'premium' ? 'Premium' : 'Pro';
    return String(label || fallback).replace(/\s+Membership$/i, '');
  }

  async function waitForConfetti() {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 3000) {
      if (typeof window.confetti === 'function') return window.confetti;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    return null;
  }

  async function fireConfetti() {
    const confetti = await waitForConfetti();
    if (!confetti) return;
    try {
      confetti({
        disableForReducedMotion: true,
        origin: { x: 0.5, y: 0.72 },
        particleCount: 100,
        spread: 90,
        startVelocity: 48,
        zIndex: 10000,
      });
    } catch {}
  }

  function focusDialog() {
    window.requestAnimationFrame(() => {
      const dialog = visibleDialog();
      const focusable = dialog?.querySelector(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      );
      focusable?.focus({ preventScroll: true });
    });
  }

  function openLayer() {
    previousFocus = document.activeElement;
    syncView();
    root.hidden = false;
    hideShopifyChat();
    lockPage(true);
    focusDialog();
  }

  function destroyPaymentElement() {
    try {
      paymentElement?.unmount?.();
      paymentElement?.destroy?.();
    } catch {}
    paymentElement = null;
    paymentFormHost.replaceChildren();
  }

  async function waitForCheckoutPresentation(requestId) {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const animations = checkout.getAnimations();
    if (!animations.length) return;
    await Promise.allSettled(animations.map((animation) => animation.finished));
    if (requestId !== activeRequest || checkout.hidden)
      throw new DOMException('Checkout closed.', 'AbortError');
  }

  function clearPolling() {
    if (pollingTimer) window.clearTimeout(pollingTimer);
    pollingTimer = undefined;
  }

  function clearSuccessCloseTimer() {
    if (successCloseTimer) window.clearTimeout(successCloseTimer);
    successCloseTimer = undefined;
  }

  function scheduleSuccessReload() {
    clearSuccessCloseTimer();
    successCloseTimer = window.setTimeout(() => {
      successCloseTimer = undefined;
      window.location.reload();
    }, 4000);
  }

  function closeLayer() {
    if (
      checkoutState === 'submitting' ||
      checkoutState === 'confirming' ||
      (!resultDialog.hidden &&
        resultDialog.dataset.resultState === 'processing')
    )
      return;
    const shouldStartPopupCooldown = context?.entry_page === 'launch';
    // A checkout id is only a restore marker while the payment form is open.
    // If the user explicitly closes an unpaid checkout, do not resurrect it
    // automatically on the next page load.
    const shouldClearUnpaidCheckout = !checkout.hidden;
    activeRequest += 1;
    abortController?.abort();
    abortController = null;
    clearPolling();
    if (checkoutState !== 'payment_success') clearSuccessCloseTimer();
    destroyPaymentElement();
    checkout.hidden = true;
    resultDialog.hidden = true;
    offer.hidden = false;
    root.hidden = true;
    root.removeAttribute('data-checkout-state');
    lockPage(false);
    restoreShopifyChat();
    checkoutState = 'closed';
    context = null;
    idempotencyKey = '';
    if (shouldClearUnpaidCheckout) setStoredCheckoutId('');
    previousFocus?.focus?.({ preventScroll: true });
    previousFocus = null;
    if (shouldStartPopupCooldown && popupSectionId) {
      markPopupClosed(popupSectionId);
    }
  }

  function getGa4Field(field, timeoutMs = 1500) {
    return new Promise((resolve) => {
      const measurementId = String(window.theme?.ga4MeasurementId || '').trim();
      if (!measurementId || typeof window.gtag !== 'function') {
        resolve(undefined);
        return;
      }
      let completed = false;
      let timer;
      const finish = (value) => {
        if (completed) return;
        completed = true;
        if (timer !== undefined) window.clearTimeout(timer);
        resolve(
          value === undefined || value === null || value === ''
            ? undefined
            : String(value),
        );
      };
      timer = window.setTimeout(() => finish(undefined), timeoutMs);
      try {
        window.gtag('get', measurementId, field, finish);
      } catch {
        finish(undefined);
      }
    });
  }

  async function getStatsigStableId(timeoutMs = 1500) {
    if (!window.statsigClient) return undefined;
    let timer;
    try {
      const ready = window.statsigReady || Promise.resolve();
      const stableId = await Promise.race([
        ready.then(() => {
          const statsigContext = window.statsigClient?.getContext?.();
          return statsigContext?.stableID || statsigContext?.stableId;
        }),
        new Promise((resolve) => {
          timer = window.setTimeout(() => resolve(undefined), timeoutMs);
        }),
      ]);
      return stableId ? String(stableId) : undefined;
    } catch {
      return undefined;
    } finally {
      if (timer !== undefined) window.clearTimeout(timer);
    }
  }

  async function getAnalytics() {
    const [gaClientId, gaSessionId, statsigStableId] = await Promise.all([
      getGa4Field('client_id'),
      getGa4Field('session_id'),
      getStatsigStableId(),
    ]);
    return {
      entryPage: context.entry_page,
      ...(gaClientId ? { gaClientId } : {}),
      ...(gaSessionId ? { gaSessionId } : {}),
      ...(statsigStableId ? { statsigStableId } : {}),
    };
  }

  function storePurchaseIntent() {
    const intent = createPurchaseIntent({
      entryPage: context.entry_page,
      returnUrl: getSafeReturnUrl(),
    });
    try {
      sessionStorage.setItem(
        PURCHASE_INTENT_STORAGE_KEY,
        JSON.stringify(intent),
      );
      sessionStorage.setItem(CTA_LOADING_STORAGE_KEY, '1');
    } catch {}
    window.location.href = getLoginUrl(intent.return_url);
  }

  function setCtaLoading(loading) {
    ctaLoading = loading;
    root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      button.classList.toggle('is-loading', loading);
      button.innerHTML = loading
        ? '<span class="jjb-membership-offer__cta-spinner" aria-hidden="true"></span><span>Loading...</span>'
        : 'Join for $19.90/mo';
    });
    try {
      if (loading) sessionStorage.setItem(CTA_LOADING_STORAGE_KEY, '1');
      else sessionStorage.removeItem(CTA_LOADING_STORAGE_KEY);
    } catch {}
  }

  async function redirectLoggedInCta() {
    const requestId = ++activeRequest;
    abortController?.abort();
    abortController = new AbortController();
    trackCheckoutMonitor('membership_popup_checkout_start');
    try {
      const data = await requestMembership('/checkouts', {
        method: 'POST',
        signal: abortController.signal,
        body: {
          plan: 'PRO',
          billingCycle: 'MONTH',
          successUrl: getCheckoutSuccessUrl('pro'),
          idempotencyKey: createIdempotencyKey(),
          analytics: await getAnalytics(),
        },
      });
      if (requestId !== activeRequest) return false;
      const checkoutId = data?.id || data?.checkoutId;
      trackCheckoutMonitor('membership_popup_checkout_success', { checkoutId });
      const url = getAirwallexPaymentUrl(data);
      if (!/^https?:\/\//i.test(url))
        throw new Error('Airwallex payment URL is unavailable.');
      trackCheckoutMonitor('membership_checkout_redirect', {
        checkoutId,
        action: context?.action_type === 'upgrade' ? 'upgrade' : 'buy',
        plan:
          data?.plan || (context?.plan_type === 'premium' ? 'PREMIUM' : 'PRO'),
        billingCycle:
          data?.billingCycle ||
          (context?.billing_cycle === 'yearly' ? 'YEAR' : 'MONTH'),
      });
      window.zlog?.flushTracking?.();
      setCtaLoading(false);
      window.location.href = url;
      return true;
    } catch (error) {
      if (error?.name !== 'AbortError' && requestId === activeRequest) {
        trackCheckoutMonitor('membership_popup_checkout_fail', {
          message: error?.message || 'Membership checkout request failed.',
        });
        showCheckoutRequestError(error);
        setCtaLoading(false);
      }
      return false;
    }
  }

  function getStoredCheckoutId() {
    try {
      const cached = JSON.parse(
        sessionStorage.getItem(CHECKOUT_ID_STORAGE_KEY),
      );
      const checkoutId = String(cached?.checkoutId || '');
      const requestedAt = Number(cached?.requestedAt || 0);
      if (
        !checkoutId ||
        !Number.isFinite(requestedAt) ||
        requestedAt <= 0 ||
        Date.now() - requestedAt > CHECKOUT_CACHE_TTL_MS
      ) {
        sessionStorage.removeItem(CHECKOUT_ID_STORAGE_KEY);
        return '';
      }
      return checkoutId;
    } catch {
      try {
        sessionStorage.removeItem(CHECKOUT_ID_STORAGE_KEY);
      } catch {}
      return '';
    }
  }

  function setStoredCheckoutId(checkoutId) {
    try {
      if (checkoutId)
        sessionStorage.setItem(
          CHECKOUT_ID_STORAGE_KEY,
          JSON.stringify({ checkoutId, requestedAt: Date.now() }),
        );
      else sessionStorage.removeItem(CHECKOUT_ID_STORAGE_KEY);
    } catch {}
  }

  async function pollCheckout(checkoutId) {
    clearPolling();
    if (!pollingStartedAt) pollingStartedAt = Date.now();
    try {
      const result = await requestMembership(
        `/checkouts/${encodeURIComponent(checkoutId)}`,
      );
      const nextState = resolveCheckoutState(result?.status);
      if (nextState === 'payment_success') {
        setStoredCheckoutId('');
        checkoutState = 'payment_success';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'success',
          `Congrats! <strong>You're now on ${getResultPlanLabel(result?.planLabel)}!</strong>`,
        );
        scheduleSuccessReload();
        await fireConfetti();
        return;
      }
      if (nextState === 'payment_failed') {
        latestPaymentError = {
          code: result?.code,
          message: result?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult('failed', 'Payment failed. Please try again.', 'Retry');
        return;
      }
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'Payment is still being confirmed.',
          'Check status',
        );
        return;
      }
      const delay = Date.now() - pollingStartedAt < 10_000 ? 1000 : 2500;
      pollingTimer = window.setTimeout(() => pollCheckout(checkoutId), delay);
    } catch {
      if (Date.now() - pollingStartedAt >= 60_000) {
        checkoutState = 'confirm_timeout';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'processing',
          'We could not confirm the result yet.',
          'Check status',
        );
      } else {
        pollingTimer = window.setTimeout(() => pollCheckout(checkoutId), 2500);
      }
    }
  }

  async function restoreCheckout(checkoutId) {
    const requestId = ++activeRequest;
    resultDialog.hidden = true;
    checkout.hidden = false;
    offer.hidden = true;
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    try {
      const data = await requestMembership(
        `/checkouts/${encodeURIComponent(checkoutId)}`,
      );
      if (requestId !== activeRequest || checkout.hidden) return;
      const nextState = resolveCheckoutState(data?.status);
      if (nextState === 'payment_success') {
        setStoredCheckoutId('');
        checkoutState = 'payment_success';
        root.dataset.checkoutState = checkoutState;
        showResult(
          'success',
          `Congrats! <strong>You're now on ${getResultPlanLabel(data?.planLabel)}!</strong>`,
        );
        scheduleSuccessReload();
        await fireConfetti();
        return;
      }
      if (nextState === 'payment_failed') {
        latestPaymentError = {
          code: data?.code,
          message: data?.message || 'Payment failed. Please try again.',
        };
        setStoredCheckoutId('');
        checkoutState = 'payment_failed';
        root.dataset.checkoutState = checkoutState;
        showResult('failed', 'Payment failed. Please try again.', 'Retry');
        return;
      }
      if (String(data?.status || '').toUpperCase() === 'ACTIVE') {
        if (!data?.clientSecret) throw new Error('Checkout is not ready.');
        await mountPaymentForm(
          { ...data, checkoutId: data.id || checkoutId },
          requestId,
        );
        return;
      }
      checkoutState = 'confirming';
      root.dataset.checkoutState = checkoutState;
      showResult('processing', 'Processing your payment...');
      pollingStartedAt = 0;
      pollCheckout(checkoutId);
    } catch (error) {
      if (requestId !== activeRequest) return;
      setCheckoutState(
        'init_error',
        `<div>${error?.message || 'Unable to restore checkout.'}</div>`,
      );
    }
  }

  function handlePaymentSubmitted(checkoutId) {
    if (!begunCheckoutIds.has(checkoutId)) {
      begunCheckoutIds.add(checkoutId);
      trackMembership('membership_begincheckout', {
        entry_page: context.entry_page,
        plan_type: context.plan_type,
        billing_cycle: context.billing_cycle,
        action_type: context.action_type,
        checkout_id: checkoutId,
      });
    }
    checkoutState = 'confirming';
    root.dataset.checkoutState = checkoutState;
    showResult('processing', 'Processing your payment...');
    pollingStartedAt = 0;
    pollCheckout(checkoutId);
  }

  async function mountPaymentForm(data, requestId) {
    syncCheckoutAmount(data);
    let sdk;
    try {
      sdk = await loadAirwallexSdk(data.sdkUrl);
    } catch (error) {
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'load', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
    if (requestId !== activeRequest || checkout.hidden) return;
    let initialized;
    try {
      initialized = await sdk.init?.({
        env: data.environment || (isTestMode() ? 'demo' : 'prod'),
        locale: document.documentElement.lang || 'en',
        enabledElements: ['billing'],
      });
    } catch (error) {
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'init', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
    const billing = initialized?.billing;
    if (!billing?.createCheckout) {
      const error = new Error('Billing Checkout is unavailable.');
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'render', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
    let billingCheckout;
    try {
      billingCheckout = await billing.createCheckout({
        client_secret: data.clientSecret || data.client_secret,
      });
      paymentElement = await billingCheckout.createElement('paymentForm');
    } catch (error) {
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'render', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
    if (!paymentElement) {
      const error = new Error('Payment component is unavailable.');
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'render', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
    paymentElement.on?.('ready', () => {
      if (requestId !== activeRequest) return;
      setCheckoutState('ready', '');
      mount.hidden = false;
      trackMembership(
        'membership_sdk_loaded',
        { load_duration_ms: Math.max(0, Date.now() - sdkLoadStartedAt) },
        { monitor: true },
      );
    });
    paymentElement.on?.('success', () =>
      handlePaymentSubmitted(data.checkoutId),
    );
    paymentElement.on?.('error', (event) => {
      if (event?.code === 'customerFormValidationFailed') {
        setCheckoutState(
          'ready',
          '<div>Please complete your address details to continue.</div>',
        );
        mount.hidden = false;
        return;
      }
      latestPaymentError = event;
      trackMembership(
        'membership_popup_pay_error',
        getAirwallexErrorParams(event, data.checkoutId),
        { monitor: true },
      );
      setCheckoutState(
        'payment_failed',
        `<div>${event?.message || 'Payment could not be submitted.'}</div>`,
      );
      showResult('failed', 'Payment failed. Please try again.', 'Retry');
    });
    // On iOS Safari, mounting a cross-origin payment iframe while its bottom
    // sheet ancestor is being transformed can leave the iframe visible but not
    // tappable. Wait until the sheet has reached its final, untransformed state.
    try {
      await waitForCheckoutPresentation(requestId);
      paymentElement.mount?.(paymentFormHost.id);
    } catch (error) {
      latestSdkError = error;
      trackMembership(
        'membership_popup_sdk_error',
        { phase: 'render', ...getAirwallexErrorParams(error, data.checkoutId) },
        { monitor: true },
      );
      throw error;
    }
  }

  async function initializeCheckout() {
    if (
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    )
      return;
    const storedCheckoutId = getStoredCheckoutId();
    if (storedCheckoutId) {
      restoreCheckout(storedCheckoutId);
      return;
    }
    const requestId = ++activeRequest;
    sdkLoadStartedAt = Date.now();
    latestSdkError = null;
    latestPaymentError = null;
    abortController?.abort();
    abortController = new AbortController();
    if (!idempotencyKey) idempotencyKey = createIdempotencyKey();
    offer.hidden = true;
    resultDialog.hidden = true;
    checkout.hidden = false;
    planLabel.textContent = `${context.plan_label} · ${context.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'}`;
    billingLabel.textContent = `Billed ${context.billing_cycle}, starting today`;
    priceLabel.textContent = context.display_price;
    checkoutSubtotalAmount.textContent = '';
    checkoutSubtotal.hidden = true;
    checkoutAdjustmentAmount.textContent = '';
    checkoutAdjustment.hidden = true;
    setCheckoutState(
      'checkout_loading',
      '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Loading secure checkout...</p></div>',
    );
    focusDialog();
    try {
      const data = await requestMembership('/checkouts', {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Idempotency-Key': idempotencyKey },
        body: {
          plan: context.plan_type === 'premium' ? 'PREMIUM' : 'PRO',
          billingCycle: context.billing_cycle === 'yearly' ? 'YEAR' : 'MONTH',
          successUrl: getCheckoutSuccessUrl(context.plan_type),
          idempotencyKey,
          analytics: await getAnalytics(),
        },
      });
      if (requestId !== activeRequest || checkout.hidden) return;
      const checkoutData = {
        ...data,
        checkoutId: data?.id || data?.checkoutId,
      };
      if (!checkoutData.checkoutId || !checkoutData.clientSecret)
        throw new Error('Checkout is not ready.');
      setStoredCheckoutId(checkoutData.checkoutId);
      await mountPaymentForm(checkoutData, requestId);
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest) return;
      showCheckoutRequestError(error);
      setCheckoutState(
        'init_error',
        `<div>${error?.message || 'Unable to initialize checkout.'}</div>`,
      );
    }
  }

  function open(options = {}) {
    if (
      checkoutState === 'checkout_loading' ||
      checkoutState === 'submitting' ||
      checkoutState === 'confirming'
    ) {
      return true;
    }
    const entryPage = normalizeEntryPage(options.entry_page);
    context = {
      entry_page: entryPage,
      plan_type: String(options.plan_type || 'pro').toLowerCase(),
      billing_cycle: normalizeCheckoutBillingCycle(options.billing_cycle),
      plan_label:
        options.plan_label ||
        (options.plan_type === 'premium'
          ? 'Premium Membership'
          : 'Pro Membership'),
      display_price: String(
        options.display_price || root.dataset.defaultCheckoutPrice || '',
      ),
      action_type: options.action_type || 'purchase',
    };
    openLayer();
    offer.hidden = false;
    checkout.hidden = true;
    resultDialog.hidden = true;
    trackMembership('membership_plan_impression', {
      entry_page: entryPage,
      style: 'popup',
    });
    return true;
  }

  function openCheckout(options = {}) {
    if (designMode) {
      open(options);
      return true;
    }
    open(options);
    context.action_type =
      options.action_type ||
      (isActivePaidMembership(getThemeMembershipState())
        ? 'upgrade'
        : 'purchase');
    initializeCheckout();
    return true;
  }

  root.querySelectorAll('[data-jjb-membership-close]').forEach((button) => {
    button.addEventListener('click', closeLayer);
  });
  root.querySelectorAll('[data-jjb-membership-join]').forEach((button) => {
    button.addEventListener('click', async () => {
      if (ctaLoading) return;
      setCtaLoading(true);
      trackMembership(
        'membership_popup_click',
        {
          entry_page: context?.entry_page,
          plan_type: 'Pro',
          billing_cycle: 'monthly',
          isLogin: isLoggedIn(),
        },
        { monitor: true },
      );
      if (!isLoggedIn()) {
        storePurchaseIntent();
        return;
      }
      await redirectLoggedInCta();
    });
  });
  checkoutClose.addEventListener('click', closeLayer);
  resultClose.addEventListener('click', closeLayer);
  function setCheckoutRetryLoading(loading) {
    retry.disabled = loading;
    retry.setAttribute('aria-busy', loading ? 'true' : 'false');
    retry.innerHTML = loading
      ? '<span class="jjb-membership-checkout__retry-spinner" aria-hidden="true"></span><span>Try again</span>'
      : 'Try again';
  }

  function setResultRetryLoading(loading) {
    resultRetry.disabled = loading;
    resultRetry.setAttribute('aria-busy', loading ? 'true' : 'false');
    resultRetry.innerHTML = loading
      ? '<span class="jjb-membership-result__retry-spinner" aria-hidden="true"></span><span>Retry</span>'
      : 'Retry';
  }

  async function redirectToAirwallexCheckout({
    keepCheckoutError = false,
  } = {}) {
    if (!context) return;
    const requestId = ++activeRequest;
    abortController?.abort();
    abortController = new AbortController();
    idempotencyKey = createIdempotencyKey();
    setStoredCheckoutId('');
    if (!keepCheckoutError) {
      showResult('processing', 'Redirecting to secure checkout...');
    }
    try {
      const data = await requestMembership('/checkouts', {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Idempotency-Key': idempotencyKey },
        body: {
          plan: context.plan_type === 'premium' ? 'PREMIUM' : 'PRO',
          billingCycle: context.billing_cycle === 'yearly' ? 'YEAR' : 'MONTH',
          successUrl: getCheckoutSuccessUrl(context.plan_type),
          idempotencyKey,
          analytics: await getAnalytics(),
        },
      });
      if (requestId !== activeRequest) return;
      const url = getAirwallexPaymentUrl(data);
      if (!url) throw new Error('Airwallex payment URL is unavailable.');
      window.location.href = url;
    } catch (error) {
      if (error?.name === 'AbortError' || requestId !== activeRequest) return;
      showCheckoutRequestError(error);
      if (!keepCheckoutError) {
        setCheckoutState(
          'init_error',
          `<div>${error?.message || 'Unable to start secure checkout.'}</div>`,
        );
      }
      return false;
    }
    return true;
  }

  retry.addEventListener('click', async () => {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        setCheckoutState(
          'confirming',
          '<div><span class="jjb-membership-spinner" aria-hidden="true"></span><p>Checking payment status...</p></div>',
        );
        pollingStartedAt = 0;
        pollCheckout(checkoutId);
      }
      return;
    }
    trackMembership(
      'membership_sdk_retry',
      getAirwallexErrorParams(latestSdkError),
      { monitor: true },
    );
    setCheckoutRetryLoading(true);
    await redirectToAirwallexCheckout({ keepCheckoutError: true });
    setCheckoutRetryLoading(false);
  });
  resultRetry.addEventListener('click', async () => {
    if (checkoutState === 'confirm_timeout') {
      const checkoutId = getStoredCheckoutId();
      if (checkoutId) {
        checkoutState = 'confirming';
        root.dataset.checkoutState = checkoutState;
        showResult('processing', 'Processing your payment...');
        pollingStartedAt = 0;
        pollCheckout(checkoutId);
      }
      return;
    }
    if (checkoutState === 'payment_failed') {
      trackMembership(
        'membership_pay_retry',
        getAirwallexErrorParams(latestPaymentError),
        { monitor: true },
      );
      setResultRetryLoading(true);
      await redirectToAirwallexCheckout({ keepCheckoutError: true });
      setResultRetryLoading(false);
      return;
    }
    setStoredCheckoutId('');
    idempotencyKey = '';
    checkoutState = 'closed';
    resultDialog.hidden = true;
    initializeCheckout();
  });
  window.addEventListener('resize', syncView);
  document.addEventListener('keydown', (event) => {
    if (root.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeLayer();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = visibleDialog();
    const items = Array.from(
      dialog?.querySelectorAll(
        'button:not([disabled]), a[href], input, [tabindex="0"]',
      ) || [],
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const storedCheckoutId = designMode ? null : getStoredCheckoutId();
  if (storedCheckoutId) {
    context = {
      entry_page: 'direct',
      plan_type: 'pro',
      billing_cycle: 'monthly',
      plan_label: 'Membership',
      action_type: 'purchase',
    };
    openLayer();
    restoreCheckout(storedCheckoutId);
  } else if (isLoggedIn()) {
    let intent;
    try {
      intent = parsePurchaseIntent(
        sessionStorage.getItem(PURCHASE_INTENT_STORAGE_KEY),
      );
      sessionStorage.removeItem(PURCHASE_INTENT_STORAGE_KEY);
    } catch {}
    if (intent && !isActivePaidMembership(getThemeMembershipState())) {
      open({
        entry_page: intent.entry_page,
        plan_type: 'pro',
        billing_cycle: 'monthly',
      });
      setCtaLoading(true);
    }
  }

  return {
    open,
    openCheckout,
    close: closeLayer,
    designMode,
    canAutoOpen: () =>
      !popupSectionId || shouldShowPopup(popupSectionId, popupDelayDays),
  };
}

function recordExperimentGroup(
  experimentName,
  key,
  value,
  groupIds,
  unknownGroupId,
) {
  const groupId = groupIds[String(value)] ?? unknownGroupId;
  window.zlog?.recordExperimentGroup?.(`${experimentName}:${key}`, groupId);
}

function getPopupSection() {
  return (
    document.querySelector('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--v2')
  );
}

function getDelaySeconds(el) {
  return (parseInt(el.dataset.delaySeconds, 10) || 5) * 1000;
}

function getDelayDays(el) {
  return parseInt(el.dataset.delayDays, 10) || 30;
}

function shouldShowPopup(sectionId, delayDays) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    const lastClosed = localStorage.getItem(key);
    if (!lastClosed) return true;
    const daysSinceClosed =
      (Date.now() - parseInt(lastClosed, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceClosed >= delayDays;
  } catch {
    return true;
  }
}

function markPopupClosed(sectionId) {
  try {
    const key = STORAGE_KEY_PREFIX + sectionId;
    localStorage.setItem(key, String(Date.now()));
  } catch {}
}

function showOriginalPopup(sectionEl) {
  const popup =
    sectionEl.closest('.newsletter-popup--original') ||
    document.querySelector('.newsletter-popup--original');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  popup.querySelectorAll('.js-modal-close').forEach((btn) => {
    btn.addEventListener('click', () => {
      popup.classList.remove('modal--is-active');
      popup.style.display = 'none';
      markPopupClosed(sectionId);
      showOriginalReminder();
    });
  });

  initOriginalReminder(popup);

  if (!shouldShowPopup(sectionId, delayDays)) {
    showOriginalReminder();
    return;
  }

  setTimeout(() => {
    popup.style.display = '';
    popup.classList.add('modal--is-active');
  }, delayMs);
}

function initOriginalReminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      wrapper.style.display = 'none';
      if (popup) {
        popup.style.display = '';
        popup.classList.add('modal--is-active');
      }
    });
  }
}

function showOriginalReminder() {
  const wrapper = document.querySelector('.newsletter-reminder--original');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

function showV2Popup(hideGiftBox = false) {
  const popup = document.querySelector('.newsletter-popup--v2');
  if (!popup) return;

  const sectionId = popup.dataset.sectionId;
  const delayDays = getDelayDays(popup);
  const delayMs = getDelaySeconds(popup);

  const closePopup = () => {
    popup.style.display = 'none';
    markPopupClosed(sectionId);
    if (!hideGiftBox) {
      showV2Reminder();
    }
  };

  popup.addEventListener('click', (e) => {
    if (
      e.target.closest('.newsletter-popup-v2__close') ||
      e.target.closest('.newsletter-popup-v2__overlay')
    ) {
      closePopup();
    }
  });

  if (!hideGiftBox) {
    initV2Reminder(popup);
  }

  if (popup.querySelector('.newsletter-v2-form-success')) {
    markPopupClosed(sectionId);
    if (window.toast) window.toast('🎁 Thanks for subscribing！');
    return;
  }

  if (popup.querySelector('.newsletter-v2-form-error')) {
    if (window.toast) window.toast('Login failed. Please try again.');
    return;
  }

  if (!shouldShowPopup(sectionId, delayDays)) {
    if (!hideGiftBox) {
      showV2Reminder();
    }
    return;
  }

  // 命中 hide_gift_box 实验组时立即展示，否则按配置延时展示
  if (hideGiftBox) {
    popup.style.display = 'block';
  } else {
    setTimeout(() => {
      popup.style.display = 'block';
    }, delayMs);
  }
}

function initV2Reminder(popup) {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;

  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.addEventListener('click', () => {
      if (reminder._wasDragged) {
        reminder._wasDragged = false;
        return;
      }
      wrapper.style.display = 'none';
      if (popup) popup.style.display = 'block';
    });
  }

  const closeBtn = wrapper.querySelector('.newsletter-reminder-v2__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      wrapper.style.display = 'none';
    });
  }
}

function showV2Reminder() {
  const wrapper = document.querySelector('.newsletter-reminder--v2');
  if (!wrapper) return;
  wrapper.style.display = '';
  const reminder = wrapper.querySelector('newsletter-reminder');
  if (reminder) {
    reminder.dataset.enabled = 'true';
  }
}

async function initPopupAbTest() {
  const membershipPopup = createMembershipPopupController();
  if (membershipPopup) window.jjbMembershipPopup = membershipPopup;
  const sectionEl = getPopupSection();

  try {
    const membershipExperiment = await getExperiment(
      MEMBERSHIP_EXPERIMENT_NAME,
    );
    const enabled =
      window.theme?.isVip === true ||
      membershipExperiment.get('enable', false) === true;
    window.zlog?.recordExperimentGroup?.(
      'show_vip_banner:enable',
      enabled ? 12 : 11,
    );
    if (
      enabled &&
      membershipPopup &&
      membershipPopup.designMode !== true &&
      membershipPopup.canAutoOpen() &&
      window.jjbMembershipCheckoutPending !== true &&
      !isActivePaidMembership(getThemeMembershipState())
    ) {
      membershipPopup.open({ entry_page: 'launch' });
      return;
    }
  } catch {}

  if (!sectionEl) return;

  let group = 'old';
  let hideGiftBox = false;
  try {
    const experiment = await getExperiment(EXPERIMENT_NAME);
    group = experiment.get('mode', 'old');
    recordExperimentGroup(
      EXPERIMENT_NAME,
      'mode',
      group,
      PROMOTION_POPUP_GROUP_IDS,
      34,
    );

    // 读取 hide_gift_box 实验：Control=false，Test=true，异常默认 false
    try {
      const hideGiftBoxExp = await getExperiment(HIDE_GIFT_BOX_EXPERIMENT_NAME);
      hideGiftBox = hideGiftBoxExp.get('enable', false) === true;
      recordExperimentGroup(
        HIDE_GIFT_BOX_EXPERIMENT_NAME,
        'enable',
        hideGiftBox,
        HIDE_GIFT_BOX_GROUP_IDS,
        37,
      );
    } catch {
      hideGiftBox = false;
    }
  } catch {
    group = 'old';
    hideGiftBox = false;
  }

  logEvent('promotion_popup_exposure', null, {
    group,
    hide_gift_box: hideGiftBox,
  });

  if (group === 'none') {
    return;
  }

  if (group === 'new') {
    showV2Popup(hideGiftBox);
  } else {
    showOriginalPopup(sectionEl);
  }
}

initPopupAbTest();
